import * as analysisApi from "@/api/analyses/index";
import * as mt from "@/store/analyses/mutation-types";
import { AnalysisDTO, Analysis, AnalysisImageAreaData } from '@/api/analyses/types';
import store from "@/store";
import * as notificationService from "../notification/index";
import i18n from "@/i18n";
import EventBus from '@/event-bus';

export async function getByUuid(uuid: string) {
  await store.dispatch(`analyses/${mt.ANALYSES_REQUEST}`);

  try {
    const analysis = (await analysisApi.getByUuid(uuid!)).data;
    await store.dispatch(`analyses/${mt.ANALYSES_REQUEST_SUCCESS}`, analysis);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      await store.dispatch(`analyses/${mt.ANALYSES_REQUEST_ERROR}`);
    } else {
      throw err;
    }
  }
}

export async function getAnalysisByFarmUuid(farmUuid: string): Promise<Analysis[]> {
  return (await analysisApi.getAnalysisByFarmUuid(farmUuid!)).data;
}

export async function createAnalysis(analysisDTO: AnalysisDTO, analysisType: string) {
  store.dispatch(`analyses/${mt.CREATE_ANALYSIS_REQUEST}`);
  try {
    await analysisApi.createAnalysis(analysisType, analysisDTO);
    store.dispatch(`analyses/${mt.CREATE_ANALYSIS_SUCCESS}`);
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.analysisMessage") as string
    );

    EventBus.$emit("new:analysis", analysisDTO.farmUuid!);

  } catch (err) {
    store.dispatch(`analyses/${mt.CREATE_ANALYSIS_FAILURE}`);
    if (err.response && err.response.status === 429) {
      notificationService.addErrorNotification(
        i18n.t("notifications.error.trial-analysis-limit-exceeded",{
          analysisCount: store.getters['user/analysisCount']
        }) as string
      );
      return;
    }
    if (err.response && err.response.status === 403) {
      notificationService.addErrorNotification(i18n.t("notifications.error.product-not-allowed") as string);
      return;
    }

    notificationService.addErrorNotification(
      i18n.t("notifications.error.message") as string
    );
     
  }
}

export async function cleanup() {
  await store.dispatch(`analyses/${mt.CLEANUP}`);
}

export async function deleteAnalysis(analysis: Analysis) {
  try {
    await analysisApi.deleteByUuid(analysis.uuid);
    await cleanup();
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.analysis-delete") as string
    );

    EventBus.$emit("new:analysis", analysis.farmUuid);

  } catch (err) {
    store.dispatch(`analyses/${mt.DELETE_ANALYSIS_FAILURE}`, analysis);
    notificationService.addErrorNotification(
      i18n.t("notifications.error.analysis-delete") as string
    );
     
  }
}

export async function deleteAnalysisWithoutStore(analysis: Analysis) {
  try {
    await analysisApi.deleteByUuid(analysis.uuid);
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.analysis-delete") as string
    );

  } catch (err) {
    notificationService.addErrorNotification(
      i18n.t("notifications.error.analysis-delete") as string
    );
     
  }
}

export async function findAreaExpansionByHarvestSeasonAndCropType(uuid: string, harvestSeason: string, cropType: string) {
  store.dispatch(`analyses/${mt.FETCHING_AREA_EXPANSION_DTO}`);

  try {
    const chartDTO = (await analysisApi.findAreaExpansionByHarvestSeasonAndCropType(uuid, harvestSeason, cropType)).data;
    store.dispatch(`analyses/${mt.FETCH_AREA_EXPANSION_DTO_SUCCESS}`, chartDTO);
  } catch (err) {
    store.dispatch(`analyses/${mt.FETCH_AREA_EXPANSION_DTO_FAILED}`);
  }
}

export async function findScoreAreaComparativeDataByAnalysis(uuid: string, harvestSeason: string, cropType: string, yieldScoreType: string) {
  
  try {
    return (await analysisApi.findScoreAreaComparativeDataByAnalysis(uuid, harvestSeason, cropType, yieldScoreType)).data;
  } catch (err) {
    return null;
  }
}


export async function getComparativeScoreByHarvestAndCropType(uuid: string, harvestSeason: string, cropType: string) {
  store.dispatch(`analyses/${mt.FETCH_COMPARATIVE_SCORE_REQUEST}`);

  try {
    const chartDTO = (await analysisApi.getComparativeScoreByHarvestAndCropType(uuid, harvestSeason, cropType)).data;
    store.dispatch(`analyses/${mt.FETCH_COMPARATIVE_SCORE_SUCCESS}`, chartDTO);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      store.dispatch(`analyses/${mt.FETCH_COMPARATIVE_SCORE_FAILED}`);
    }
  }
}

export async function findYieldByYearData(uuid: string, harvestSeason: string, cropType: string) {
  store.dispatch(`analyses/${mt.FETCHING_YIELD_BY_YEAR_DTO}`);

  try {
    const chartDTO = (await analysisApi.findYieldByYearData(uuid, harvestSeason, cropType)).data;
    store.dispatch(`analyses/${mt.FETCH_YIELD_BY_YEAR_DTO_SUCCESS}`, chartDTO);
  } catch (err) {
      store.dispatch(`analyses/${mt.FETCH_YIELD_BY_YEAR_DTO_FAILED}`);
  }
}


export async function getAnalysisSummary(uuid: string, harvestSeason: string, cropType: string) {
  store.dispatch(`analyses/${mt.FETCH_ANALYSIS_SUMMARY_REQUEST}`);

  try {
    const analysisSummaryDTO = (await analysisApi.getAnalysisSummary(uuid, harvestSeason, cropType)).data;
    store.dispatch(`analyses/${mt.FETCH_ANALYSIS_SUMMARY_SUCCESS}`, analysisSummaryDTO);
  } catch (err) {
      store.dispatch(`analyses/${mt.FETCH_ANALYSIS_SUMMARY_FAILED}`);
  }
}

export async function getAnalysisCropsStages(uuid: string, harvestSeason: string, cropType: string) {
  store.dispatch(`analyses/${mt.FETCH_ANALYSIS_CROPS_STAGES_REQUEST}`);

  try {
    const cropsStagesDTO = (await analysisApi.getAnalysisCropsStages(uuid, harvestSeason, cropType)).data;
    store.dispatch(`analyses/${mt.FETCH_ANALYSIS_CROPS_STAGES_SUCCESS}`, cropsStagesDTO);
  } catch (err) {
    store.dispatch(`analyses/${mt.FETCH_ANALYSIS_CROPS_STAGES_FAILED}`);
  }
}


export async function getFarmAnalysisYieldEvolution(farmAnalysisUuid: string, harvestSeason = 'SUMMER', cropType = 'SOY') {
  await store.dispatch(`analyses/${mt.FETCH_ANALYSIS_YIELD_EVOLUTION_REQUEST}`);

  try {
    const dto = (await analysisApi.getFarmAnalysisYieldEvolution(farmAnalysisUuid, harvestSeason, cropType)).data;
    await store.dispatch(`analyses/${mt.FETCH_ANALYSIS_YIELD_EVOLUTION_SUCCESS}`, dto);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      await store.dispatch(`analyses/${mt.FETCH_ANALYSIS_YIELD_EVOLUTION_FAILED}`);
    }
  }
}

export async function getLastYearYieldData(uuid: string, harvestSeason = 'SUMMER', cropType = 'SOY') {
  await store.dispatch(`analyses/${mt.FETCH_LAST_YEAR_YIELD_REQUEST}`);

  try {
    const lastYearYield = (await analysisApi.getLastYearYieldData(uuid, harvestSeason, cropType)).data;
    await store.dispatch(`analyses/${mt.FETCH_LAST_YEAR_YIELD_SUCCESS}`, lastYearYield)
  } catch (err) {
    if (err.response && err.response.status === 404) {
      await store.dispatch(`analyses/${mt.FETCH_LAST_YEAR_YIELD_FAILED}`);
    }
  }
}


export async function getAnalysisImageData(farmAnalysisUuid: string) : Promise<AnalysisImageAreaData[]>{
  return (await analysisApi.getAnalysisImageAreaData(farmAnalysisUuid)).data;
}

export async function getSentinelaPrimeLastImageinfo(farmAnalysisUuid: string) : Promise<AnalysisImageAreaData>{
  return (await analysisApi.getSentinelaPrimeLastImageInfo(farmAnalysisUuid)).data;
}

export async function updateMonitoring(uuid: string){
  await analysisApi.updateMonitoring(uuid);
}
export async function updateAnalysis(uuid: string, year: number){
  await analysisApi.updateAnalysis(uuid, year);
}


